import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { message } from "antd";
import { Hooks, Util, Context } from "@xt/xtbase";
import { get_balances } from "@/api/v4/balance";
import {
  get_convertConfigList,
  post_convertPreview,
  post_convertOrder,
} from "@/api/v4/convert";
import { resolveAllowedFiatCodesForDisplay, isFiatMarketCode } from "@/utils/fiatMarket";
import Storage from "utils/storage";
import { get_symbol } from "api/v4/market";
import {
  resolveConvertMode,
  resolveConvertType,
  resolveCurrencyId,
  findConfigForPair,
  resolveToCodesForFrom,
  buildPreviewPayload,
  buildUniversalOrderPayload,
  resolvePairErrorKey,
  resolveConvertPreviewErrorKey,
  resolveFromCodesFromConfigs,
  inferIsFiatFromConfigs,
} from "../convertV2";
import styles from "./index.module.less";
import ConvertForm from "../ConvertForm";
import ConvertFormFooter from "../ConvertFormFooter";
import { fetchConvertFromBalanceMap } from "../convertBalanceUtils";
import ConvertBuySellBar from "../ConvertBuySellBar";
// import ConvertKeypadBottom from "../ConvertKeypadBottom";
import ConvertConfirmSheet from "../ConvertConfirmSheet";
import { resolveConvertKlineSymbol } from "../convertKlineSymbol";
import { parseKlinePairCodes, useConvertKlineClosePrice, formatConvertKlineDisplayPrice, formatConvertFallbackRatePrice, formatKlineClosePrice, trimTrailingZeros, isConvertReceiveAmountDisplayZero } from "../convertKlineShared";
import { getConvertRouterPath } from "../convertResultShared";

const ConvertKlineSection = dynamic(() => import("../ConvertKlineSection"), { ssr: false });

const { useTranslation } = Hooks;
const { getUrl, debounce } = Util;
const { XtContext } = Context;

function isFiatCurrencyCode(currency) {
  return isFiatMarketCode(currency);
}

function buildFromOptions(fromCodes, allOptions, allowedFiatState) {
  if (!fromCodes.length) return [];
  const codeSet = new Set(fromCodes);
  const opts = allOptions.filter((o) => codeSet.has(o.code));
  return sortCurrencyOptions(filterFiatByAllowed(opts, allowedFiatState), allowedFiatState);
}

function buildToOptionsForFrom(fromCode, allOptions, configs, allowedFiatState) {
  const toCodes = resolveToCodesForFrom(fromCode, configs);
  if (!toCodes.length) return [];
  const codeSet = new Set(toCodes);
  const opts = allOptions.filter((o) => codeSet.has(o.code));
  return sortCurrencyOptions(filterFiatByAllowed(opts, allowedFiatState), allowedFiatState);
}

function sortCurrencyOptions(opts, allowedFiatState) {
  const orderMap = new Map((allowedFiatState?.allowedCodes || []).map((c, i) => [c, i]));
  return [...opts].sort((a, b) => {
    if (a.isFiat && b.isFiat) {
      return (orderMap.get(a.code) ?? 999) - (orderMap.get(b.code) ?? 999);
    }
    if (a.isFiat !== b.isFiat) return a.isFiat ? -1 : 1;
    return a.code.localeCompare(b.code);
  });
}

function filterFiatByAllowed(opts, allowedFiatState) {
  if (!allowedFiatState?.allowedCodes?.length) return opts;
  const allowed = new Set(allowedFiatState.allowedCodes);
  const filtered = opts.filter((o) => !o.isFiat || allowed.has(o.code));
  return filtered.length ? filtered : opts;
}

/** To 占位虚值 / 到账展示；支持仅 min 或仅 max */
function formatReceiveAmountRange(minVal, maxVal) {
  const min = parseConfigAmount(minVal);
  const max = parseConfigAmount(maxVal);
  if (min == null && max == null) return "";
  const minStr = min != null ? formatConvertKlineDisplayPrice(String(min)) : "";
  const maxStr = max != null ? formatConvertKlineDisplayPrice(String(max)) : "";
  if (minStr && maxStr) return `${minStr} - ${maxStr}`;
  return minStr || maxStr;
}

/** 无 K 线时 Price 参考试算金额：优先 minAmount，否则固定 1 */
function resolveReferencePreviewAmount(selectedConfig) {
  const min = selectedConfig?.minAmount;
  if (min != null && String(min).trim() !== "") {
    const n = parseFloat(String(min).replace(/,/g, ""));
    if (Number.isFinite(n) && n > 0) {
      return String(min).replace(/,/g, "").trim();
    }
  }
  return "1";
}

/** 解析 config 金额字段；缺失或非法时返回 null */
function parseConfigAmount(value) {
  if (value == null || String(value).trim() === "") return null;
  const n = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** 格式化金额展示（接口为字符串），法币 2 位，Crypto 最多 8 位；支持仅 min 或仅 max */
function formatAmountRange(minVal, maxVal, isCrypto = false) {
  const min = parseConfigAmount(minVal);
  const max = parseConfigAmount(maxVal);
  if (min == null && max == null) return "";
  const frac = isCrypto ? 8 : 2;
  const fmt = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: frac });
  if (min != null && max != null) return `${fmt(min)} - ${fmt(max)}`;
  if (min != null) return fmt(min);
  return fmt(max);
}

/**
 * 试算/到账金额展示用小数位数（仅用于非接口直接返回的兜底展示）：
 * - Crypto：展示 8 位小数
 * - 法币：展示 2 位小数
 */
const DISPLAY_DECIMALS = { crypto: 8, fiat: 2 };

/** 按接口返回的字符串展示，去掉末尾多余的 0；法币时最多 2 位小数再 trim */
function formatApiAmount(str, currency) {
  if (str == null || str === "") return "";
  const s = String(str).replace(/,/g, "").trim();
  if (!s || !/^-?\d+(\.\d+)?$/.test(s)) return "";
  const isFiat = currency && isFiatCurrencyCode(currency);
  if (isFiat) {
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return "";
    return n.toFixed(2).replace(/\.?0+$/, "") || "0";
  }
  return trimTrailingZeros(s) || "0";
}

/** 按参考字符串的小数位数格式化数字，去掉末尾多余的 0 */
function formatWithSameDecimals(num, referenceStr) {
  if (!Number.isFinite(num)) return "";
  const ref = referenceStr != null ? String(referenceStr) : "";
  const dec = ref.includes(".") ? (ref.split(".")[1] || "").length : 0;
  const s = num.toFixed(dec).replace(/\.?0+$/, "");
  return s || "0";
}

function formatConvertDisplayAmount(amountStr, currency) {
  if (amountStr == null || amountStr === "") return "";
  const n = parseFloat(String(amountStr).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "";
  const decimals = isFiatCurrencyCode(currency) ? DISPLAY_DECIMALS.fiat : DISPLAY_DECIMALS.crypto;
  const s = n.toFixed(decimals).replace(/\.?0+$/, "");
  return s || "0";
}

/** Price 展示区固定保留 2 位小数 */
function formatPriceDisplay(rateStr) {
  if (rateStr == null || rateStr === "") return "";
  const n = parseFloat(String(rateStr).replace(/,/g, "").trim());
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2);
}

/** 根据汇率把 source 范围换算为 target 范围；缺失侧返回 null */
function sourceRangeToTargetRange(minSource, maxSource, exchangeRate, isBuy) {
  const rate = parseFloat(String(exchangeRate ?? 0));
  if (!rate || rate <= 0) return { min: null, max: null };
  const min = parseConfigAmount(minSource);
  const max = parseConfigAmount(maxSource);
  const convert = (n) => (isBuy ? n / rate : n * rate);
  return {
    min: min != null ? convert(min) : null,
    max: max != null ? convert(max) : null,
  };
}

/** 试算防抖延迟（ms），用户停止输入后再请求 */
const PREVIEW_DEBOUNCE_MS = 500;
/** visualViewport 高度缩减超过该值视为软键盘已展开 */
const MOBILE_KEYBOARD_HEIGHT_THRESHOLD = 120;

/** 兼容其他页面引用（法币展示由接口 config list 驱动，此处不再维护静态列表） */
export const FIAT_OPTIONS = [];

function resolveInitialFromCurrency({ embedded, fromQuery, tabFromQuery }) {
  if (typeof fromQuery === "string" && fromQuery) return fromQuery.toUpperCase();
  if (embedded) return "USDT";
  if (tabFromQuery === "sell") return "USDT";
  if (tabFromQuery === "swap") return "USDT";
  return "AED";
}

function resolveInitialToCurrency({ embedded, toQuery, tabFromQuery }) {
  if (typeof toQuery === "string" && toQuery) return toQuery.toUpperCase();
  if (embedded) return "BTC";
  if (tabFromQuery === "sell") return "AED";
  if (tabFromQuery === "swap") return "BTC";
  return "USDT";
}

export default function ConvertBuy({ embedded = false }) {
  const t = useTranslation();
  const router = useRouter();
  const tabFromQuery = router.query.tab;
  const fromQuery = router.query.from;
  const toQuery = router.query.to;
  const [fromCurrency, setFromCurrency] = useState(() =>
    resolveInitialFromCurrency({ embedded, fromQuery, tabFromQuery })
  );
  const [toCurrency, setToCurrency] = useState(() =>
    resolveInitialToCurrency({ embedded, toQuery, tabFromQuery })
  );
  const [spendAmount, setSpendAmount] = useState("");
  const [chartOpen, setChartOpen] = useState(false);
  const [klineOption, setKlineOption] = useState({ interval: "1", chartType: 3 });
  const [spendAvailable, setSpendAvailable] = useState("");
  const [fromBalanceMap, setFromBalanceMap] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [convertConfigList, setConvertConfigList] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [cfdSymbolList, setCfdSymbolList] = useState([]);
  /** 与 web-index 法币充值一致：已充值(fiatCurrencyIds) + IP + 有余额，用于过滤和排序法币选项 */
  const [allowedFiatState, setAllowedFiatState] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErrorKey, setPreviewErrorKey] = useState(null);
  /** 无 K 线时 Price 参考试算（minAmount 或 1），与用户输入试算分离 */
  const [referencePreviewResult, setReferencePreviewResult] = useState(null);
  const [confirmPreviewResult, setConfirmPreviewResult] = useState(null);
  const [confirmPreviewLoading, setConfirmPreviewLoading] = useState(false);
  const previewRequestIdRef = useRef(0);
  const referencePreviewRequestIdRef = useRef(0);
  const confirmPreviewRequestIdRef = useRef(0);
  /** 用户手动关闭 K 线后，不因配置就绪 effect 再次自动展开 */
  const userDismissedChartRef = useRef(false);
  /** 因 From 输入框聚焦而隐藏 K 线，键盘收起后恢复展示 */
  const chartHiddenByInputRef = useRef(false);
  /** 上一次试算得到的到账金额，loading 时继续展示不闪退 */
  const lastStableReceiveAmountRef = useRef("");

  const [appState] = React.useContext(XtContext);
  const loginStatus = appState?.loginStatus;

  /** v2.5：convertType 1–7；下单统一 POST /convert/order */
  const enabledConfigs = useMemo(
    () => (convertConfigList || []).filter((c) => c.enabled !== 0),
    [convertConfigList]
  );

  const convertMode = useMemo(
    () => resolveConvertMode(fromCurrency, toCurrency),
    [fromCurrency, toCurrency]
  );

  const klineSymbol = useMemo(
    () => resolveConvertKlineSymbol(fromCurrency, toCurrency, convertMode, cfdSymbolList),
    [fromCurrency, toCurrency, convertMode, cfdSymbolList]
  );

  const klineAvailable = !!klineSymbol;

  const klineInterval = klineOption.interval;
  const klineChartType = klineOption.chartType;

  const klineClosePrice = useConvertKlineClosePrice(klineSymbol, klineInterval);
  const { quote: klineQuoteCode } = useMemo(
    () => parseKlinePairCodes(klineSymbol),
    [klineSymbol]
  );

  const convertType = useMemo(
    () => resolveConvertType(fromCurrency, toCurrency),
    [fromCurrency, toCurrency]
  );

  const currencyOptionMap = useMemo(() => {
    const map = new Map();
    enabledConfigs.forEach((c) => {
      const upsert = (code, name, patch = {}) => {
        const upper = (code || "").toUpperCase();
        if (!upper) return;
        const prev = map.get(upper) || { code: upper, name: name || upper, color: "#26A69A" };
        map.set(upper, { ...prev, name: name || prev.name, ...patch });
      };
      if (c.currencyCode) {
        upsert(c.currencyCode, c.currencyDisplayName, {
          currencyId: c.currencyId,
        });
      }
      if (c.cryptoCurrencyCode) {
        upsert(c.cryptoCurrencyCode, c.cryptoCurrencyDisplayName, {
          currencyId: c.cryptoCurrencyId,
          cryptoCurrencyId: c.cryptoCurrencyId,
        });
      }
    });
    map.forEach((opt, code) => {
      opt.isFiat = inferIsFiatFromConfigs(code, enabledConfigs);
    });
    return map;
  }, [enabledConfigs]);

  const allCurrencyOptions = useMemo(() => {
    const codes = new Set();
    enabledConfigs.forEach((c) => {
      if (c.currencyCode) codes.add(String(c.currencyCode).toUpperCase());
      if (c.cryptoCurrencyCode) codes.add(String(c.cryptoCurrencyCode).toUpperCase());
    });
    const opts = [...codes].map((code) => currencyOptionMap.get(code)).filter(Boolean);
    return sortCurrencyOptions(filterFiatByAllowed(opts, allowedFiatState), allowedFiatState);
  }, [enabledConfigs, currencyOptionMap, allowedFiatState]);

  const fromCodes = useMemo(
    () => resolveFromCodesFromConfigs(enabledConfigs),
    [enabledConfigs]
  );

  const fromOptions = useMemo(
    () => buildFromOptions(fromCodes, allCurrencyOptions, allowedFiatState),
    [fromCodes, allCurrencyOptions, allowedFiatState]
  );
  const toOptions = useMemo(
    () => buildToOptionsForFrom(fromCurrency, allCurrencyOptions, enabledConfigs, allowedFiatState),
    [fromCurrency, allCurrencyOptions, enabledConfigs, allowedFiatState]
  );

  const fromCurrencyId = useMemo(
    () => resolveCurrencyId(fromCurrency, currencyOptionMap, enabledConfigs),
    [fromCurrency, currencyOptionMap, enabledConfigs]
  );

  const toCurrencyId = useMemo(
    () => resolveCurrencyId(toCurrency, currencyOptionMap, enabledConfigs),
    [toCurrency, currencyOptionMap, enabledConfigs]
  );

  const selectedConfig = useMemo(
    () => findConfigForPair(enabledConfigs, fromCurrency, toCurrency),
    [enabledConfigs, fromCurrency, toCurrency]
  );

  const pairErrorKey = useMemo(
    () => resolvePairErrorKey(fromCurrency, toCurrency, convertMode, !!selectedConfig),
    [fromCurrency, toCurrency, convertMode, selectedConfig]
  );

  const sourceLimits = useMemo(
    () => ({
      min: parseConfigAmount(selectedConfig?.minAmount),
      max: parseConfigAmount(selectedConfig?.maxAmount),
    }),
    [selectedConfig]
  );

  /** BUY: 限制为法币范围；SELL/SWAP: 限制为加密货币范围 */
  const spendRange = useMemo(() => {
    if (!selectedConfig) return "";
    const isCryptoSource = convertMode === "sell" || convertMode === "swap";
    return formatAmountRange(selectedConfig.minAmount, selectedConfig.maxAmount, isCryptoSource);
  }, [selectedConfig, convertMode]);

  /** 到账侧限额：优先用接口换算好的 targetMinAmount / targetMaxAmount，避免前端按汇率折算 */
  const receiveRange = useMemo(() => {
    if (!selectedConfig) return "";
    const tMin = selectedConfig.targetMinAmount;
    const tMax = selectedConfig.targetMaxAmount;
    const targetMin = parseConfigAmount(tMin);
    const targetMax = parseConfigAmount(tMax);
    if (targetMin != null || targetMax != null) {
      return formatReceiveAmountRange(tMin, tMax);
    }
    const { min, max } = sourceRangeToTargetRange(
      selectedConfig.minAmount,
      selectedConfig.maxAmount,
      selectedConfig.exchangeRate,
      convertMode === "buy"
    );
    return formatReceiveAmountRange(min, max);
  }, [selectedConfig, convertMode]);

  const spendCurrency = fromCurrency;
  const receiveCurrency = toCurrency;
  const receiveAmountRaw = previewResult?.targetAmount ?? "";
  const receiveAmount = receiveAmountRaw ? formatConvertKlineDisplayPrice(receiveAmountRaw) : "";
  const hasSpendInput = spendAmount && String(spendAmount).trim();
  if (receiveAmountRaw) {
    const formatted = formatConvertKlineDisplayPrice(receiveAmountRaw);
    if (formatted && formatted !== "0") lastStableReceiveAmountRef.current = formatted;
  }
  const displayReceiveAmount = !hasSpendInput
    ? ""
    : previewLoading
      ? (lastStableReceiveAmountRef.current || "")
      : (receiveAmount === "0" ? "" : receiveAmount);
  const receiveAmountForDisplay = displayReceiveAmount === "0" ? "" : displayReceiveAmount;

  const effectivePreviewErrorKey = useMemo(() => {
    if (previewErrorKey) return previewErrorKey;
    if (
      hasSpendInput &&
      !previewLoading &&
      previewResult?.targetAmount != null &&
      isConvertReceiveAmountDisplayZero(previewResult.targetAmount)
    ) {
      return "convert.errorAmountTooSmallAfterFees";
    }
    return null;
  }, [previewErrorKey, hasSpendInput, previewLoading, previewResult]);

  const config = {
    spendRange,
    receiveRange,
    spendCurrency,
    receiveCurrency,
    receiveAmount: receiveAmountForDisplay,
    receiveAmountConfirm: receiveAmountForDisplay || formatConvertDisplayAmount(spendAmount, receiveCurrency) || "",
    receiveAmountSuccess: receiveAmountForDisplay || formatConvertDisplayAmount(spendAmount, receiveCurrency) || "",
  };
  const { receiveAmountConfirm, receiveAmountSuccess } = config;

  const spendMin = sourceLimits.min;
  const spendMax = sourceLimits.max;

  useEffect(() => {
    let cancelled = false;
    setConfigLoading(true);
    get_convertConfigList()
      .then((res) => {
        if (cancelled) return;
        const raw = res?.result ?? res?.data ?? res;
        const list = Array.isArray(raw) ? raw : [];
        setConvertConfigList(list);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error("convert config list", e);
          setConvertConfigList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cached = Storage.get("cfdSymbols") || {};
    if (cached.symbols?.length) {
      setCfdSymbolList(cached.symbols.map((item) => item.symbol));
    }
    get_symbol({
      params: {
        version: cached.version,
        types: "cfd",
      },
    })
      .then((data) => {
        if (data?.symbols?.length) {
          setCfdSymbolList(data.symbols.map((item) => item.symbol));
          if (data.version !== cached.version) {
            Storage.set("cfdSymbols", data);
          }
        }
      })
      .catch(() => {});
  }, []);

  /** 法币展示直接跟随推荐币种缓存，旧的余额/IP 二次判断不再参与过滤。 */
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      const { allowedCodes, fromFiatIds, fromIP } = await resolveAllowedFiatCodesForDisplay({
        fiatCurrencyIds: appState?.userInfo?.fiatCurrencyIds,
      });
      if (cancelled) return;
      setAllowedFiatState({ allowedCodes, fromFiatIds, fromIP });
    };
    run();
    return () => { cancelled = true; };
  }, [loginStatus, appState?.userInfo?.fiatCurrencyIds]);

  useEffect(() => {
    if (configLoading || !fromOptions.length) return;
    const hasCurrent = fromOptions.some((o) => o.code === (fromCurrency || "").toUpperCase());
    if (!hasCurrent && fromOptions[0]) setFromCurrency(fromOptions[0].code);
  }, [configLoading, fromOptions, fromCurrency]);

  useEffect(() => {
    if (configLoading || !fromCurrency || !toOptions.length) return;
    const hasCurrent = toOptions.some((o) => o.code === (toCurrency || "").toUpperCase());
    if (!hasCurrent && toOptions[0]) setToCurrency(toOptions[0].code);
  }, [configLoading, fromCurrency, toOptions, toCurrency]);

  const resetAmountPreview = useCallback(() => {
    setSpendAmount("");
    setPreviewResult(null);
    setPreviewErrorKey(null);
    lastStableReceiveAmountRef.current = "";
  }, []);

  const clearPreviewState = useCallback(() => {
    setPreviewResult(null);
    setPreviewErrorKey(null);
    lastStableReceiveAmountRef.current = "";
  }, []);

  const handleFromChange = useCallback(
    (code) => {
      setFromCurrency((code || "").toUpperCase());
      resetAmountPreview();
    },
    [resetAmountPreview]
  );

  const handleToChange = useCallback(
    (code) => {
      setToCurrency((code || "").toUpperCase());
      clearPreviewState();
    },
    [clearPreviewState]
  );

  const handleSwapDirection = useCallback(() => {
    const nextFrom = (toCurrency || "").toUpperCase();
    const nextTo = (fromCurrency || "").toUpperCase();
    if (!nextFrom || !nextTo) return;

    const reversedToOptions = buildToOptionsForFrom(
      nextFrom,
      allCurrencyOptions,
      enabledConfigs,
      allowedFiatState
    );
    if (!reversedToOptions.some((o) => o.code === nextTo)) {
      message.warning(t("convert.swapNotSupported"));
      return;
    }

    setFromCurrency(nextFrom);
    setToCurrency(nextTo);
    resetAmountPreview();
  }, [
    fromCurrency,
    toCurrency,
    allCurrencyOptions,
    enabledConfigs,
    allowedFiatState,
    resetAmountPreview,
    t,
  ]);

  const fetchSpendBalance = useCallback(async () => {
    if (!loginStatus || !spendCurrency) {
      setSpendAvailable("");
      return;
    }
    try {
      const res = await get_balances({
        params: { currencies: spendCurrency.toUpperCase() },
        isResFull: true,
      });
      const result = res?.result;
      let available = "0";
      if (result?.assets && Array.isArray(result.assets)) {
        const asset = result.assets.find(
          (a) => (a.currency || "").toUpperCase() === spendCurrency.toUpperCase()
        );
        if (asset && (asset.availableAmount != null || asset.available != null)) {
          available = String(asset.availableAmount ?? asset.available ?? "0");
        }
      } else if (result && typeof result === "object" && result[spendCurrency.toUpperCase()]) {
        const a = result[spendCurrency.toUpperCase()];
        available = String(a.availableAmount ?? a.available ?? "0");
      } else if (result?.totalUsdtAmount != null && spendCurrency.toUpperCase() === "USDT") {
        available = String(result.totalUsdtAmount);
      }
      setSpendAvailable(available);
    } catch (e) {
      console.error("convert fetch balance", e);
      setSpendAvailable("0");
    }
  }, [loginStatus, spendCurrency]);

  const fetchFromBalanceMap = useCallback(async () => {
    if (!loginStatus || !fromOptions.length) {
      setFromBalanceMap(null);
      return;
    }
    try {
      const map = await fetchConvertFromBalanceMap(fromOptions);
      setFromBalanceMap(map);
    } catch (e) {
      console.error("convert fetch from balance map", e);
      setFromBalanceMap({});
    }
  }, [loginStatus, fromOptions]);

  useEffect(() => {
    fetchFromBalanceMap();
  }, [fetchFromBalanceMap]);

  useEffect(() => {
    fetchSpendBalance();
  }, [fetchSpendBalance]);

  const fetchPreview = useCallback((params) => {
    const { currencyId, cryptoCurrencyId, type, sourceAmount } = params;
    const requestId = ++previewRequestIdRef.current;
    setPreviewLoading(true);
    setPreviewErrorKey(null);
    post_convertPreview({ currencyId, cryptoCurrencyId, type, sourceAmount })
      .then((res) => {
        if (requestId !== previewRequestIdRef.current) return;
        const result = res?.result ?? res?.data ?? res;
        if (result && result.targetAmount != null) {
          setPreviewResult(result);
          setPreviewErrorKey(
            isConvertReceiveAmountDisplayZero(result.targetAmount)
              ? "convert.errorAmountTooSmallAfterFees"
              : null
          );
        } else {
          setPreviewResult(null);
        }
      })
      .catch((err) => {
        if (requestId !== previewRequestIdRef.current) return;
        setPreviewResult(null);
        setPreviewErrorKey(resolveConvertPreviewErrorKey(err?.mc));
      })
      .finally(() => {
        if (requestId !== previewRequestIdRef.current) return;
        setPreviewLoading(false);
      });
  }, []);

  const debouncedFetchPreview = useMemo(
    () => debounce(fetchPreview, PREVIEW_DEBOUNCE_MS),
    [fetchPreview]
  );

  useEffect(() => {
    if (!fromCurrencyId || !toCurrencyId || !convertType || pairErrorKey || !spendAmount) {
      setPreviewResult(null);
      setPreviewErrorKey(null);
      return;
    }
    const amountNum = parseFloat(String(spendAmount).replace(/,/g, ""));
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setPreviewResult(null);
      setPreviewErrorKey(null);
      return;
    }
    debouncedFetchPreview(
      buildPreviewPayload(
        fromCurrency,
        toCurrency,
        String(spendAmount),
        currencyOptionMap,
        enabledConfigs
      )
    );
  }, [
    fromCurrencyId,
    toCurrencyId,
    convertType,
    pairErrorKey,
    spendAmount,
    fromCurrency,
    toCurrency,
    currencyOptionMap,
    enabledConfigs,
    debouncedFetchPreview,
  ]);

  /** 无 K 线：切换币对时用 minAmount（或 1）调用 preview，供 Price 实时汇率兜底 */
  useEffect(() => {
    if (klineSymbol) {
      setReferencePreviewResult(null);
      return undefined;
    }
    if (!fromCurrencyId || !toCurrencyId || !convertType || pairErrorKey) {
      setReferencePreviewResult(null);
      return undefined;
    }

    const requestId = ++referencePreviewRequestIdRef.current;
    const sourceAmount = resolveReferencePreviewAmount(selectedConfig);

    post_convertPreview(
      buildPreviewPayload(fromCurrency, toCurrency, sourceAmount, currencyOptionMap, enabledConfigs)
    )
      .then((res) => {
        if (requestId !== referencePreviewRequestIdRef.current) return;
        const result = res?.result ?? res?.data ?? res;
        if (result?.exchangeRate != null) setReferencePreviewResult(result);
        else setReferencePreviewResult(null);
      })
      .catch(() => {
        if (requestId !== referencePreviewRequestIdRef.current) return;
        setReferencePreviewResult(null);
      });

    return () => {
      referencePreviewRequestIdRef.current += 1;
    };
  }, [
    klineSymbol,
    fromCurrencyId,
    toCurrencyId,
    convertType,
    pairErrorKey,
    fromCurrency,
    toCurrency,
    currencyOptionMap,
    enabledConfigs,
    selectedConfig,
    selectedConfig?.minAmount,
  ]);

  const loginQuery = typeof window !== "undefined" ? "?backurl=" + encodeURIComponent(window.location.href) : "";

  useEffect(() => {
    if (!router.isReady) return;
    const from =
      typeof fromQuery === "string" && fromQuery ? fromQuery.toUpperCase() : "";
    const to = typeof toQuery === "string" && toQuery ? toQuery.toUpperCase() : "";
    if (from || to) {
      if (from) setFromCurrency(from);
      if (to) setToCurrency(to);
      resetAmountPreview();
      return;
    }
    if (tabFromQuery === "sell") {
      setFromCurrency("USDT");
      setToCurrency("AED");
    } else if (tabFromQuery === "swap") {
      setFromCurrency("USDT");
      setToCurrency("BTC");
    } else if (embedded) {
      setFromCurrency("USDT");
      setToCurrency("BTC");
    }
  }, [router.isReady, tabFromQuery, fromQuery, toQuery, embedded, resetAmountPreview]);

  // 点击 Buy/Sell 打开弹窗时调用一次试算（不是点击弹窗内 Confirm 时）
  useEffect(() => {
    if (!confirmOpen) {
      setConfirmPreviewResult(null);
      setConfirmPreviewLoading(false);
      return;
    }
    if (!fromCurrencyId || !toCurrencyId || !convertType || !spendAmount) return;
    const amountNum = parseFloat(String(spendAmount).replace(/,/g, ""));
    if (!Number.isFinite(amountNum) || amountNum <= 0) return;
    const requestId = ++confirmPreviewRequestIdRef.current;
    setConfirmPreviewLoading(true);
    setConfirmPreviewResult(null);
    post_convertPreview(
      buildPreviewPayload(fromCurrency, toCurrency, String(spendAmount), currencyOptionMap, enabledConfigs)
    )
      .then((res) => {
        if (requestId !== confirmPreviewRequestIdRef.current) return;
        const result = res?.result ?? res?.data ?? res;
        if (result && result.targetAmount != null) setConfirmPreviewResult(result);
        else setConfirmPreviewResult(null);
      })
      .catch(() => {
        if (requestId !== confirmPreviewRequestIdRef.current) return;
        setConfirmPreviewResult(null);
      })
      .finally(() => {
        if (requestId !== confirmPreviewRequestIdRef.current) return;
        setConfirmPreviewLoading(false);
      });
  }, [
    confirmOpen,
    fromCurrencyId,
    toCurrencyId,
    convertType,
    spendAmount,
    fromCurrency,
    toCurrency,
    currencyOptionMap,
    enabledConfigs,
  ]);

  const CONVERT_ORDER_STORAGE_KEY = "convert_order_result";

  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem(CONVERT_ORDER_STORAGE_KEY);
      }
    } catch (e) {}
  }, []);

  const saveOrderResult = (payload) => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(CONVERT_ORDER_STORAGE_KEY, JSON.stringify(payload));
      }
    } catch (e) {
      console.warn("[convert] localStorage.setItem failed", e);
    }
  };

  const handleConfirmSubmit = () => {
    const sourceAmount = String(spendAmount ?? "").trim();
    const spendCur = fromCurrency;
    const receiveCur = toCurrency;
    const previewData = confirmPreviewResult || previewResult || {};
    const previewTarget = previewData.targetAmount ?? "";
    const previewExchangeRate = previewData.exchangeRate ?? "";
    const previewTotalFee = previewData.totalFee ?? "";
    const previewFeeCurrency = previewData.feeCurrencyCode ?? "";
    const orderType = Number(previewData.convertType ?? convertType ?? 0);

    const buildOrderResultPayload = () => ({
      orderType,
      spendAmount: sourceAmount,
      spendCurrency: spendCur,
      receiveAmountSuccess: previewTarget,
      receiveCurrency: receiveCur,
      exchangeRate: previewExchangeRate,
      totalFee: previewTotalFee,
      feeCurrencyCode: previewFeeCurrency,
      state: "PENDING",
      previewResult: previewData,
    });

    const orderPayload = buildUniversalOrderPayload(
      fromCurrency,
      toCurrency,
      sourceAmount,
      currencyOptionMap,
      enabledConfigs
    );

    if (!fromCurrencyId || !toCurrencyId || !sourceAmount) {
      saveOrderResult(buildOrderResultPayload());
      router.push(getConvertRouterPath("/convert/result/processing"));
      return;
    }

    saveOrderResult(buildOrderResultPayload());

    const submitOrder = post_convertOrder(orderPayload);

    submitOrder
      .then((res) => {
        const result = res?.result ?? res?.data ?? res;
        return result || {};
      })
      .catch(() => {
        saveOrderResult({
          ...buildOrderResultPayload(),
          state: "FAILED",
        });
        router.push(getConvertRouterPath("/convert/result/fail"));
        return null;
      })
      .then((apiResult) => {
        if (apiResult == null) return;
        const state = (apiResult.state ?? "").toUpperCase();
        const payload = {
          orderType: Number(apiResult.convertType ?? apiResult.type ?? orderType),
          spendAmount: sourceAmount,
          spendCurrency: spendCur,
          receiveAmountSuccess: apiResult.targetAmount ?? previewTarget,
          receiveCurrency: receiveCur,
          exchangeRate: apiResult.exchangeRate ?? previewExchangeRate,
          totalFee: apiResult.totalFee ?? previewTotalFee,
          feeCurrencyCode: apiResult.feeCurrencyCode ?? previewFeeCurrency,
          state,
          orderId: apiResult.orderId,
          result: apiResult,
        };
        saveOrderResult(payload);
        if (state === "SUCCEED") {
          router.push(getConvertRouterPath("/convert/result/success"));
        } else if (state === "FAILED") {
          router.push(getConvertRouterPath("/convert/result/fail"));
        } else {
          router.push(getConvertRouterPath("/convert/result/processing"));
        }
      });
  };

  const amountNum = spendAmount ? parseFloat(String(spendAmount).replace(/,/g, "")) : NaN;
  const hasAmount = !Number.isNaN(amountNum) && amountNum > 0;
  const availableNum = parseFloat(String(spendAvailable).replace(/,/g, "")) || 0;
  let canPreview = hasAmount && !!selectedConfig && !pairErrorKey && !effectivePreviewErrorKey;
  if (canPreview) {
    if (amountNum > availableNum) canPreview = false;
    else if (spendMin != null && amountNum < spendMin) canPreview = false;
    else if (spendMax != null && amountNum > spendMax) canPreview = false;
  }

  const priceCurrency = klineQuoteCode || receiveCurrency;
  const priceAmount = useMemo(() => {
    if (klineSymbol) {
      return klineClosePrice ? formatKlineClosePrice(klineClosePrice) : "";
    }
    const exchangeRate =
      previewResult?.exchangeRate ??
      referencePreviewResult?.exchangeRate ??
      selectedConfig?.exchangeRate;
    if (exchangeRate == null || exchangeRate === "") return "";
    const n = parseFloat(String(exchangeRate).replace(/,/g, ""));
    if (!Number.isFinite(n) || n === 0) return "";
    if (convertMode === "buy") {
      return formatKlineClosePrice(String(1 / n));
    }
    return formatKlineClosePrice(String(n));
  }, [
    klineSymbol,
    klineClosePrice,
    previewResult?.exchangeRate,
    referencePreviewResult?.exchangeRate,
    selectedConfig?.exchangeRate,
    convertMode,
  ]);

  const handleKlineOptionChange = useCallback((option) => {
    setKlineOption({
      interval: option?.interval || "1",
      chartType: option?.chartType ?? 3,
    });
  }, []);

  const handleInputActivate = useCallback(() => {
    chartHiddenByInputRef.current = true;
    userDismissedChartRef.current = true;
    setChartOpen(false);
  }, []);

  const showChartAfterInput = useCallback(() => {
    if (!chartHiddenByInputRef.current || !klineAvailable) return;
    chartHiddenByInputRef.current = false;
    userDismissedChartRef.current = false;
    setChartOpen(true);
  }, [klineAvailable]);

  const handleInputDeactivate = useCallback(() => {
    if (typeof window !== "undefined" && window.visualViewport) return;
    window.requestAnimationFrame(() => {
      if (document.activeElement?.tagName === "INPUT") return;
      showChartAfterInput();
    });
  }, [showChartAfterInput]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;

    let baselineHeight = vv.height;

    const syncBaseline = () => {
      baselineHeight = vv.height;
    };

    const handleViewportChange = () => {
      const keyboardOpen = baselineHeight - vv.height > MOBILE_KEYBOARD_HEIGHT_THRESHOLD;
      if (keyboardOpen) return;

      baselineHeight = Math.max(baselineHeight, vv.height);
      showChartAfterInput();
    };

    window.addEventListener("orientationchange", syncBaseline);
    vv.addEventListener("resize", handleViewportChange);
    vv.addEventListener("scroll", handleViewportChange);

    return () => {
      window.removeEventListener("orientationchange", syncBaseline);
      vv.removeEventListener("resize", handleViewportChange);
      vv.removeEventListener("scroll", handleViewportChange);
    };
  }, [showChartAfterInput]);

  const handleCandlestickClick = useCallback(() => {
    if (!klineAvailable) {
      message.warning(t("convert.noChart"));
      return;
    }
    setChartOpen((v) => {
      const next = !v;
      userDismissedChartRef.current = !next;
      if (!next) chartHiddenByInputRef.current = false;
      return next;
    });
  }, [klineAvailable, t]);

  useEffect(() => {
    userDismissedChartRef.current = false;
    chartHiddenByInputRef.current = false;
  }, [fromCurrency, toCurrency]);

  useEffect(() => {
    if (configLoading) return;
    if (userDismissedChartRef.current) return;
    setChartOpen(klineAvailable);
  }, [klineAvailable, fromCurrency, toCurrency, configLoading]);

  const showChart = chartOpen && klineAvailable;

  return (
    <div
      className={`${styles["convert-buy"]} ${showChart ? styles["convert-buy--chart-open"] : ""} ${embedded ? styles["convert-buy--embedded"] : ""}`}
    >
      <ConvertBuySellBar chartOpen={showChart} onCandlestickClick={handleCandlestickClick} embedded={embedded} />
      {klineAvailable && (
        <div
          className={showChart ? undefined : styles["convert-buy__kline-hidden"]}
          aria-hidden={!showChart}
        >
          <ConvertKlineSection
            fromCurrency={fromCurrency}
            toCurrency={toCurrency}
            convertMode={convertMode}
            klineSymbol={klineSymbol}
            interval={klineInterval}
            chartType={klineChartType}
            onOptionChange={handleKlineOptionChange}
          />
        </div>
      )}
      <ConvertForm
        spendAmount={spendAmount}
        setSpendAmount={setSpendAmount}
        spendCurrency={spendCurrency}
        receiveCurrency={receiveCurrency}
        spendRange={config.spendRange}
        receiveRange={config.receiveRange}
        receiveAmount={config.receiveAmount}
        spendAvailable={spendAvailable}
        spendMin={spendMin}
        spendMax={spendMax}
        fromOptions={fromOptions}
        toOptions={toOptions}
        fromBalanceMap={fromBalanceMap}
        showFromBalances={!!loginStatus}
        onFromDrawerOpen={fetchFromBalanceMap}
        fromCurrency={fromCurrency}
        toCurrency={toCurrency}
        onFromChange={handleFromChange}
        onToChange={handleToChange}
        onSwapDirection={handleSwapDirection}
        onInputActivate={handleInputActivate}
        onInputDeactivate={handleInputDeactivate}
        previewErrorKey={effectivePreviewErrorKey}
      />

      <ConvertFormFooter
        canPreview={canPreview}
        onPreview={() => setConfirmOpen(true)}
        loginQuery={loginQuery}
        previewErrorKey={effectivePreviewErrorKey}
      />

      {/* {!showChart && <ConvertKeypadBottom spendAmount={spendAmount} setSpendAmount={setSpendAmount} />} */}

      <ConvertConfirmSheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        spendCurrency={spendCurrency}
        spendAmount={spendAmount}
        receiveCurrency={receiveCurrency}
        previewData={confirmPreviewResult || previewResult}
        loading={confirmPreviewLoading}
        convertMode={convertMode}
        klineSymbol={klineSymbol}
        klineClosePrice={klineClosePrice}
        priceCurrency={priceCurrency}
        priceAmount={priceAmount}
        currencyOptionMap={currencyOptionMap}
        cfdSymbolList={cfdSymbolList}
      />
    </div>
  );
}
